import { take, call, put, actionChannel } from 'redux-saga/effects';
import { buffers } from 'redux-saga';
import * as types from '../../../lib/actionTypes.es6';
import * as actions from './actions.es6';
import { normalizeError } from '../../../components/CommissionsShared/makeWatcher.es6';
import {
  getCommissionTransactionsByAgent,
  getPolicyByGuid,
  getCustomerInfo,
  getCommissionRule,
  getComplexCustomerPremiums
} from '../../../lib/requests.es6';
import { monthRange } from './statementMonth.es6';
import {
  buildStatementRows,
  policyGuidsToResolve,
  ruleGuidsToResolve,
  customerGuidsToResolve
} from './statementRows.es6';

// Assembling one agent's statement for one month.
//
// The commission service holds the money but not the policy, so each line is
// widened from three other services. Every lookup is keyed by guid and
// deduplicated first, so forty commissions under one rule cost one rule call.
//
// Neither lookup can fail the statement. A policy, customer, premium or rule
// that does not answer leaves its columns blank and the commission still
// prints, because the money is real whether or not the other service was up.

var PAGE_SIZE = 100;
// The statement is one agent for one month. A cap stops a mistyped filter
// walking an entire history, and the UI says so when it bites.
var MAX_PAGES = 20;

function rowsOf(response) {
  var data = response && response.data ? response.data : response;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.data || data.items || data.rows || data.results || [];
}

// Every commission line for the agent in the period, following pagination.
export function* fetchAllTransactions(agentGuid, range) {
  var collected = [];
  for (var page = 1; page <= MAX_PAGES; page += 1) {
    var response = yield call(
      getCommissionTransactionsByAgent,
      agentGuid,
      Object.assign({ page: page, size: PAGE_SIZE }, range)
    );
    var rows = rowsOf(response);
    collected = collected.concat(rows);
    if (rows.length < PAGE_SIZE) break;
  }
  return collected;
}

// One lookup, its failure contained: the caller gets null rather than an error.
export function* safeLookup(apiFn, guid) {
  try {
    var response = yield call(apiFn, guid);
    return response && response.data ? response.data : null;
  } catch (e) {
    return null;
  }
}

export function* resolveMany(apiFn, guids) {
  // redux-saga is pinned at 0.14.1, which has no `all` effect: yielding an
  // array is how you run effects in parallel there. Importing `all` gets you
  // undefined and a TypeError the first time a statement is built.
  var results = yield guids.map(function(guid) {
    return call(safeLookup, apiFn, guid);
  });
  var byGuid = {};
  guids.forEach(function(guid, index) {
    if (results[index]) byGuid[guid] = results[index];
  });
  return byGuid;
}

function premiumsOfCustomer(customerGuid) {
  return getComplexCustomerPremiums({ guid: customerGuid });
}

// Premiums are fetched per customer — the endpoint takes customer_guid and
// rejects policy_guid — but a statement line names its premium by guid, so the
// returned lists are flattened into one map keyed by the premium's own guid.
export function* resolvePremiums(customerGuids) {
  var lists = yield customerGuids.map(function(guid) {
    return call(safeLookup, premiumsOfCustomer, guid);
  });
  var byGuid = {};
  lists.forEach(function(list) {
    (list || []).forEach(function(premium) {
      if (premium && premium.guid) byGuid[premium.guid] = premium;
    });
  });
  return byGuid;
}

export function* buildStatement(action) {
  var request = action.payload || {};
  var range = monthRange(request.month);

  if (!request.agentGuid || !range) {
    yield put(
      actions.getCommissionStatementFailed({
        message: 'Pick an agent and a statement month.'
      })
    );
    return;
  }

  try {
    var transactions = yield call(
      fetchAllTransactions,
      request.agentGuid,
      range
    );

    var first = yield [
      call(resolveMany, getPolicyByGuid, policyGuidsToResolve(transactions)),
      call(resolveMany, getCommissionRule, ruleGuidsToResolve(transactions))
    ];
    var policies = first[0];
    var rules = first[1];

    var customerGuids = customerGuidsToResolve(policies);
    var second = yield [
      call(resolveMany, getCustomerInfo, customerGuids),
      call(resolvePremiums, customerGuids)
    ];

    yield put(
      actions.getCommissionStatementSuccess({
        month: request.month,
        agentGuid: request.agentGuid,
        rows: buildStatementRows(transactions, {
          policies: policies,
          rules: rules,
          customers: second[0],
          premiums: second[1]
        }),
        truncated: transactions.length >= PAGE_SIZE * MAX_PAGES
      })
    );
  } catch (e) {
    yield put(actions.getCommissionStatementFailed(normalizeError(e)));
  }
}

export function* getCommissionStatementRequestWatcher() {
  var channel = yield actionChannel(
    types.GET_COMMISSION_STATEMENT_REQUEST,
    buffers.expanding()
  );
  for (;;) {
    var action = yield take(channel);
    yield* buildStatement(action);
  }
}
